import { useState } from 'react';
import { Book, Globe, Clock, FileCheck, Star, Mail } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Accordion from '../components/common/Accordion';
import Footer from '../components/common/Footer';


export default function ThesisWritingService() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [openAccordion, setOpenAccordion] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log({ name, email, subject, message });
  };

  const faqItems = [
    {
      title: "What is Thesis Writing in a PhD?",
      content: "Thesis writing in PhD refers to the process of conducting original research and documenting the findings in a structured academic paper. It is a crucial requirement for earning a doctoral degree and demonstrates a scholar's expertise in their field."
    },
    {
      title: "Process of Writing a Thesis simply?",
      content: (
        <ol className="list-decimal pl-5 space-y-2">
          <li>Choose a Topic – Select a research area that interests you and has enough scope for study.</li>
          <li>Do Background Research – Review existing literature to identify gaps and refine your research question.</li>
          <li>Prepare a Research Proposal – Outline your objectives, methodology, and expected outcomes.</li>
          <li>Collect Data – Gather relevant information through experiments, surveys, or existing sources.</li>
          <li>Analyze Data – Interpret your findings using appropriate methods and tools.</li>
          <li>Write the Thesis – Structure it into chapters (Introduction, Literature Review, Methodology, Findings, Conclusion, etc).</li>
          <li>Edit & Proofread – Ensure clarity, coherence, and proper formatting.</li>
          <li>Check for Plagiarism – Verify originality using plagiarism detection tools.</li>
          <li>Submit & Defend – Present your thesis before an academic committee for evaluation.</li>
        </ol>
      )
    },
    {
      title: "What does a proper thesis require?",
      content: "A thesis statement is a concise, clear sentence that presents the main argument or central idea of your research. It acts as a roadmap for your thesis, guiding the reader on what to expect."
    },
    {
      title: "What is a Thesis Statement?",
      content: "A thesis statement is the first sentence in your paper. It should state what you will say in the paper, i.e., what the problem area is, what solution you propose for it, etc. Thesis statements are often used at graduate school when students are asked to write essays on topics outside their majors. However, they can also be used during the early stages of research when developing an argumentative essay or another writing assignment that requires argumentation."
    },
    {
      title: "Why is a PhD Thesis Important?",
      content: "It is one of the most important documents you will ever write in your life. You will have to present all your research results, conclusions, and recommendations in one document. That's why you need to be very careful when writing it. Theses vary according to universities and departments and they differ not only in length but also in format. Some have titles such as 'thesis', while others have titles such as 'dissertation.' While they sound similar, they are quite different from each other. Thesis are written with the main focus on academic research projects that take place at universities or colleges worldwide. They are written by students who want to get their doctorate degree for further studies or employment purposes."
    },
    {
      title: "Why is Thesis Writing considered tough?",
      content: "Thesis Writing can be tough. It is a lot like writing an essay, but there are some important differences. A thesis is longer and more technical than an essay, and the topic you choose for the thesis will be more specific than in your other papers."
    },
    {
      title: "What should a Thesis Statement include?",
      content: "Your thesis statement should answer two main questions: Why do I want to write this paper? And what does it all mean? With these two questions in mind, you can begin crafting your thesis statement."
    },
    {
      title: "Examples of Thesis Statement?",
      content: (
        <div className="space-y-4">
          <p>This study examines the impact of social media on academic performance, arguing that excessive use negatively affects students' concentration and learning outcomes.</p>
          <p>A strong thesis statement is:</p>
          <ul className="list-none space-y-2">
            <li className="flex items-center">
              <span className="text-yellow-50 mr-2">✅</span>
              <span>Specific – Focuses on a clear topic.</span>
            </li>
            <li className="flex items-center">
              <span className="text-yellow-50 mr-2">✅</span>
              <span>Debatable – Presents an argument or perspective.</span>
            </li>
            <li className="flex items-center">
              <span className="text-yellow-50 mr-2">✅</span>
              <span>Concise – Usually one or two sentences long.</span>
            </li>
          </ul>
          <p>It appears in the introduction of a thesis and is supported by evidence throughout the research.</p>
        </div>
      )
    },
    {
      title: "Format of a Thesis?",
      content: (
        <div className="space-y-4">
          <p>There are two main formats for writing a thesis. Thesis with a thesis statement and thesis without a thesis statement.</p>
          <p>Thesis with a thesis statement: This format is used in most common universities, where you are required to write your dissertation in one of three formats. Thesis statements are made up of three main parts:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Introduction:</strong> It introduces the topic, problem, or questions that you want to address in your thesis. This is usually about two paragraphs long and contains all the relevant information about what your study will be about.
            </li>
            <li>
              <strong>Methodology:</strong> Describes how you are going to address the problem or question that you have mentioned above in your introduction section as well as introduce any new research tools or techniques that you plan on using in this research project.
            </li>
            <li>
              <strong>Conclusion:</strong> Summarizes all the main points of what you have discussed throughout the entire research project and gives some suggestions for future work based on this research project.
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-richblue-5 text-richblue-700">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Thesis Writing Service</h1>
          <p className="text-xl text-richblue-500">
            Advance your academic career with our research-based thesis writing assistance
          </p>
        </div>
      </div>

      {/* Services Overview */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Thesis Writing <HighlightText text={"Services"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Book className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Research-Focused</h3>
              <p className="text-richblue-500">
                Comprehensive literature review and in-depth research conducted by PhDs in your field of study.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Globe className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Global Expertise</h3>
              <p className="text-richblue-500">
                Access to global academic resources and experts from prestigious universities worldwide.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Clock className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Timely Delivery</h3>
              <p className="text-richblue-500">
                Strict adherence to deadlines with progress updates throughout the writing process.
              </p>
            </div>

            {/* Service Card 4 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <FileCheck className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Quality Assured</h3>
              <p className="text-richblue-500">
                Rigorous quality checks, plagiarism prevention, and compliance with academic standards.
              </p>
            </div>

            {/* Service Card 5 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Star className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Expert Writers</h3>
              <p className="text-richblue-500">
                Specialized writers with advanced degrees in various academic disciplines.
              </p>
            </div>

            {/* Service Card 6 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Mail className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Dedicated Support</h3>
              <p className="text-richblue-500">
                24/7 communication with your dedicated thesis advisor and support team.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Thesis Writing <HighlightText text={"Process"} /></h2>
          
          <div className="max-w-4xl mx-auto">
            {/* Process Step 1 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Consultation & Requirements</h3>
                <p className="text-richblue-500">
                  We begin with a detailed consultation to understand your research objectives, academic requirements, and timeline.
                </p>
              </div>
            </div>

            {/* Process Step 2 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Research & Outline</h3>
                <p className="text-richblue-500">
                  Our experts conduct comprehensive research and develop a detailed outline for your approval.
                </p>
              </div>
            </div>

            {/* Process Step 3 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Writing & Development</h3>
                <p className="text-richblue-500">
                  We write your thesis with a focus on clarity, academic rigor, and original research contribution.
                </p>
              </div>
            </div>

            {/* Process Step 4 */}
            <div className="flex">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Review & Finalization</h3>
                <p className="text-richblue-500">
                  Multiple rounds of review, editing, and refinement to ensure a polished final document.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-richblue-600">Ready to Get Started?</h2>
              <p className="text-richblue-500 mb-6">
                Contact our thesis writing experts today for a free consultation about your academic needs.
              </p>
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-3">Why Choose Our Service:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <div className="text-richblue-400 mr-2">✓</div>
                    <span className="text-richblue-500">Experienced PhD level writers</span>
                  </li>
                  <li className="flex items-center">
                    <div className="text-richblue-400 mr-2">✓</div>
                    <span className="text-richblue-500">Strict confidentiality</span>
                  </li>
                  <li className="flex items-center">
                    <div className="text-richblue-400 mr-2">✓</div>
                    <span className="text-richblue-500">Unlimited revisions</span>
                  </li>
                  <li className="flex items-center">
                    <div className="text-richblue-400 mr-2">✓</div>
                    <span className="text-richblue-500">Plagiarism-free guarantee</span>
                  </li>
                  <li className="flex items-center">
                    <div className="text-richblue-400 mr-2">✓</div>
                    <span className="text-richblue-500">On-time delivery promise</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
              <h3 className="text-xl font-bold mb-4 text-richblue-600">Request Information</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-richblue-500 mb-2">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-richblue-500 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-richblue-500 mb-2">Subject Area</label>
                  <select
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900"
                    required
                  >
                    <option value="">Select a Subject</option>
                    <option value="Business">Business & Management</option>
                    <option value="Computer Science">Computer Science</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Humanities">Humanities</option>
                    <option value="Medicine">Medicine & Healthcare</option>
                    <option value="Sciences">Natural Sciences</option>
                    <option value="Social Sciences">Social Sciences</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-richblue-500 mb-2">Project Details</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full bg-richblue-100 border border-richblue-300 rounded p-3 text-richblue-900 h-32"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-richblue-400 text-white font-semibold py-3 px-6 rounded-md hover:bg-richblue-500 transition-all duration-200"
                >
                  Request Information
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-richblack-800 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked <HighlightText text={"Questions"} /></h2>
          <div className="max-w-4xl mx-auto">
            {faqItems.map((item, index) => (
              <Accordion
                key={index}
                title={item.title}
                content={item.content}
                isOpen={openAccordion === index}
                onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </div>
      <Footer/>
    </div>
  );
}